import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { Text, Gap, Spacing } from '@reservamos/elements';
import { useTranslation } from 'react-i18next';
import { When } from 'react-if';
import { ACCEPTED_DOCUMENT_TYPES, isGratuityDocumentCategory } from 'utils/freeFareDocumentRules';

// 3 MB, el mismo tope que aplica `documentSelected`.
const MAX_DOCUMENT_BYTES = 3 * 1024 * 1024;

/**
 * FileSelectField component.
 *
 * Renders a titled upload control for the ANTT gratuidade document, and — unlike the first
 * version of this field — renders the validation error as readable text. Before, a rejected file
 * only turned the button's border red: the passenger saw a red box and no reason, and "Continuar"
 * stayed clickable while navigation was blocked in silence.
 *
 * @param {Object} props - The component props.
 * @param {string} props.type - The type of the input field.
 * @param {Object} props.input - The input props from redux-form.
 * @param {Object} props.meta - The meta props from redux-form.
 * @param {string} [props.busCategory] - The passenger's category, used to choose the copy. Absent
 *   keeps the gratuidade wording, which is what this field said before.
 * @returns {JSX.Element} The rendered component.
 */
const FileSelectField = ({ type, input, meta, busCategory }) => {
  delete input.value;

  const hiddenInputId = `${input.name}.file-select`;
  // Identifies the current selection, so an older read cannot overwrite a newer one.
  const selectionRef = useRef(0);
  const [passengerDocument, setPassengerDocument] = useState();
  const [fileLabelProperties, setFileLabelProperties] = useState({});
  const { t } = useTranslation('passengers');
  /**
   * Delegate event to value(s).
   * @param {string} onChange - The function to be called when the input changes.
   */
  function delegateEventToFiles(onChange) {
    return (e) => {
      // Each selection carries its own number. A read still in flight that finishes AFTER the
      // passenger picked another file no longer writes: without this, choosing a valid file and
      // then an oversized one left the second rejected on screen and the first one in the form, so
      // the document submitted was not the one the passenger could see selected.
      selectionRef.current += 1;
      const selection = selectionRef.current;
      const isCurrent = () => selection === selectionRef.current;

      const firstSelectedDocument = e.target?.files?.[0] || undefined;

      // Sin esto, un input que llega sin archivos revienta con TypeError dentro de readAsDataURL.
      if (!firstSelectedDocument) {
        onChange('');
        setPassengerDocument(undefined);
        setFileLabelProperties({});
        return;
      }

      // The size used to be checked only AFTER reading the whole file into base64, so a 200 MB
      // file was loaded into memory and into the store just to be rejected. Measured first now.
      if (firstSelectedDocument.size > MAX_DOCUMENT_BYTES) {
        onChange(`fileSize:${firstSelectedDocument.size}::`);
        setPassengerDocument(undefined);
        setFileLabelProperties({
          name: firstSelectedDocument.name || '',
          size: firstSelectedDocument.size,
          type: firstSelectedDocument.type || '',
        });
        return;
      }

      const file = new FileReader();

      // `onload` was the only hook. If the read blew up -- file moved, permissions, memory --
      // `onChange` never fired: the field stayed empty, with no message and the button looking the
      // same, and the passenger could not tell why they were stuck.
      const failRead = () => {
        if (!isCurrent()) return;

        onChange('');
        setPassengerDocument(undefined);
        setFileLabelProperties({});
      };

      file.onerror = failRead;
      file.onabort = failRead;

      file.onload = () => {
        if (!isCurrent()) return;

        const fileSize = firstSelectedDocument?.size || '';
        const fileResultWithSize = `fileSize:${fileSize}::${file.result}`;

        onChange(fileResultWithSize);
        setPassengerDocument(fileResultWithSize);
        setFileLabelProperties({
          name: firstSelectedDocument?.name || '',
          size: fileSize,
          type: firstSelectedDocument?.type || '',
        });
      };

      file.readAsDataURL(firstSelectedDocument);
    };
  }

  /**
   * Render the file label content.
   *
   * If the file label properties are empty, return the upload file text.
   * Otherwise, return the selected file name.
   *
   * Highlight the label with a red border if there is an error.
   *
   * There is an error when the file size is greater than 3 MB.
   * @returns {string} The label content.
   */
  function renderFileLabelContent() {
    const hasError = Boolean(meta.touched && meta.error);
    const fileSize = fileLabelProperties?.size;
    const fileSizeInMB = fileSize / (1024 * 1024);
    const isSizeValid = !fileSize || (fileSize && fileSizeInMB <= 3);

    const buttonStyle = {
      cursor: 'pointer',
      border: `1px solid ${hasError || !isSizeValid ? '#bf0811' : '#c2c2c2'}`,
      borderRadius: '5px',
      padding: '5px 10px',
      backgroundColor: 'white',
      boxShadow: '0 2px 3px #ccc',
    };

    const textContentStyle = {
      backgroundColor: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    };

    return (
      <>
        <Gap columnGap="M">
          <div style={buttonStyle}>
            <Text elementType="span" size="S" color={hasError ? 'accent' : 'grayMedium'}>
              {t('choose_file')}
            </Text>
          </div>
          <When condition={Object.keys(fileLabelProperties).length}>
            <div style={textContentStyle}>
              <Text elementType="span" size="S" color={isSizeValid ? 'grayMedium' : 'accent'}>
                {`${fileLabelProperties.name} (${`${fileSizeInMB.toFixed(2)} MB`})`}
              </Text>
            </div>
          </When>
        </Gap>
      </>
    );
  }

  // The same list the validator uses, imported rather than copied. `image/svg+xml` is out of it:
  // an SVG carries script, and under ACQ-93 these objects sit in a bucket that downloads without
  // credentials. `application/octet-stream` is deliberately NOT here -- the validator tolerates it,
  // but offering it in `accept` would make the picker show every file.
  const acceptedFileTypes = ACCEPTED_DOCUMENT_TYPES;

  /**
   * The copy, chosen by who is being asked for the file.
   *
   * This field does not appear only on a gratuidade: with `SHOW_DOCUMENT_EXCLUDING_ADULTS` off it
   * is shown to everyone, and the legacy gate reaches paid discounts such as `student` or
   * `teacher`. Calling it "gratuidade document" to someone who does not travel on that benefit
   * tells them something untrue, and gives them reason to think the field is not for them.
   *
   * Without `busCategory` the gratuidade wording is kept, which is what was there before.
   *
   * @param {string} key - The base key.
   * @returns {string} The key to translate.
   */
  const copyKey = (key) =>
    busCategory !== undefined && !isGratuityDocumentCategory(busCategory) ? `${key}_generic` : key;

  // The validator returns an i18n key. `defaultValue` keeps any other message readable rather than
  // printing a raw key, so this cannot regress into showing `document_required` to a passenger.
  const errorMessage =
    meta.touched && meta.error
      ? t(copyKey(meta.error), { defaultValue: t(meta.error, { defaultValue: meta.error }) })
      : null;

  return (
    <>
      <Spacing vertical size="XS">
        <Text size="S" weight="bold">
          {t(copyKey('document_upload_title'))}
        </Text>
        <Text size="XS" color="grayMedium">
          {t(copyKey('document_upload_help'))}
        </Text>
      </Spacing>

      <label className="custom-file-input" htmlFor={hiddenInputId}>
        {renderFileLabelContent()}
      </label>
      <input
        type={type}
        accept={acceptedFileTypes.join(', ')}
        onChange={delegateEventToFiles(input.onChange)}
        id={hiddenInputId}
        className="hidden"
      />

      <When condition={Boolean(errorMessage)}>
        <Text size="XS" color="accent">
          {errorMessage}
        </Text>
      </When>

      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <Field {...input} value={passengerDocument} type="hidden" component="input" />
    </>
  );
};

FileSelectField.propTypes = {
  type: PropTypes.string.isRequired,
  busCategory: PropTypes.string,
  // Redux-form props
  input: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
};

FileSelectField.defaultProps = {
  busCategory: undefined,
};

export default FileSelectField;
